import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useMemo, type ComponentProps } from "react";
import { hiddenUntilScrollClass, useScrollActivity } from "@tellescope/react-hooks";

import { OverlayLayerProvider, useOverlayZIndex } from "../../lib/overlay-z-index";
import { cn } from "../../lib/utils";
import { DialogCloseButton } from "./DialogCloseButton";
import { DialogContentContext, useDialogContext } from "./DialogContext";
import { DialogOverlay } from "./DialogOverlay";
import { DialogPortal } from "./DialogPortal";
import { dialogContentVariants, type DialogOverlayVariant, type DialogSize } from "./styles";

export type DialogContentProps = ComponentProps<typeof DialogPrimitive.Content> & {
  /** @default true */
  showCloseButton?: boolean;
  /**
   * Whether a click or focus outside the dialog closes it. Elements marked
   * `data-interactive-with-dialog` (and their descendants) never close it, so UI rendered outside
   * the dialog can be used alongside it.
   * @default true
   */
  closeOnInteractOutside?: boolean;
  /**
   * Draws a divider between `Dialog.Header`, each `Dialog.Section`, and `Dialog.Footer`, with each
   * region padding itself. Without it, the content pads and gaps its children as a whole.
   * @default false
   */
  divided?: boolean;
  /**
   * `dim` darkens what's behind the dialog. `frosted` washes it out and blurs it, and drops the
   * dialog's shadow.
   * @default "dim"
   */
  overlay?: DialogOverlayVariant;
  /**
   * The maximum width from the `sm` breakpoint up. `wide` fits side-by-side panes, such as a list
   * next to a preview of rich content.
   * @default "default"
   */
  size?: DialogSize;
};

export const DialogContent = ({
  className,
  children,
  showCloseButton = true,
  closeOnInteractOutside = true,
  divided = false,
  overlay = "dim",
  size = "default",
  onInteractOutside,
  onScroll,
  style,
  ...props
}: DialogContentProps) => {
  const { placement } = useDialogContext();
  const { isScrolling, onScroll: trackScrollActivity } = useScrollActivity();
  const { z, style: zStyle, className: zClassName } = useOverlayZIndex(placement === "contained" ? "raised" : "modal");
  const contentContext = useMemo(() => ({ divided }), [divided]);

  const handleInteractOutside: DialogContentProps["onInteractOutside"] = (event) => {
    const { target } = event.detail.originalEvent;
    if (!closeOnInteractOutside || (target instanceof Element && target.closest("[data-interactive-with-dialog]"))) {
      event.preventDefault();
    }
    onInteractOutside?.(event);
  };

  return (
    <DialogPortal>
      <DialogOverlay overlay={overlay} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        style={{ ...style, ...zStyle }}
        className={cn(
          dialogContentVariants({ placement, size, overlay, divided }),
          hiddenUntilScrollClass(isScrolling),
          zClassName,
          className
        )}
        onInteractOutside={handleInteractOutside}
        onScroll={(event) => {
          trackScrollActivity();
          onScroll?.(event);
        }}
        {...props}
      >
        <OverlayLayerProvider base={z}>
          <DialogContentContext.Provider value={contentContext}>{children}</DialogContentContext.Provider>
          {showCloseButton && <DialogCloseButton />}
        </OverlayLayerProvider>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
};
